import type {
  ConfidenceMethod,
  Criticality,
  RelationshipRole,
  RelationshipStatus,
} from '@aurora-pvi/contracts';
import type { Rng } from './rng';
import { idFor } from './ids';
import { SOURCE_SYSTEMS, bandFor, provenanceFactory } from './provenance';
import type {
  Application,
  ApplicationProcessLink,
  Process,
  Product,
  ProductApplicationLink,
  ProductDependencyLink,
  ProductProcessLink,
  RelationshipBase,
  Team,
  TeamProductLink,
} from './types';
import { at, groupBy, pad, round } from './support';

/**
 * The many-to-many graph.
 *
 * This file is where the platform's central claim either holds or does not.
 * Seven of the curated applications support more than one product on purpose —
 * the data hub supports four — because "an application supporting three
 * products is counted once in any ancestor roll-up" is only a real QA gate if
 * the fixture actually contains such applications.
 *
 * It is also where the gaps live. Roughly 7% of applications are left with no
 * product at all, a slice of relationships stay Proposed rather than Approved,
 * and some allocation percentages are deliberately null. A portfolio tool whose
 * demo data is fully mapped is demonstrating the one situation its customers do
 * not have.
 */

const CURATED_PRODUCT_APPLICATIONS: Record<
  string,
  readonly (readonly [string, RelationshipRole])[]
> = {
  'PRD-CDR': [
    ['APP-0013', 'Primary'],
    ['APP-0011', 'Supporting'],
    ['APP-0010', 'Supporting'],
    ['APP-0012', 'Supporting'],
    ['APP-0014', 'Dependency'],
    ['APP-0018', 'Dependency'],
    ['APP-0029', 'Shared'],
    ['APP-0039', 'Dependency'],
    ['APP-0037', 'Dependency'],
    ['APP-0040', 'Dependency'],
  ],
  'PRD-CRWP': [
    ['APP-0010', 'Primary'],
    ['APP-0011', 'Primary'],
    ['APP-0014', 'Supporting'],
    ['APP-0037', 'Dependency'],
  ],
  'PRD-CRTR': [['APP-0014', 'Primary']],
  'PRD-BOOK': [
    ['APP-0002', 'Primary'],
    ['APP-0001', 'Supporting'],
    ['APP-0003', 'Supporting'],
    ['APP-0004', 'Supporting'],
    ['APP-0031', 'Shared'],
    ['APP-0032', 'Dependency'],
  ],
  'PRD-TRIP': [
    ['APP-0001', 'Primary'],
    ['APP-0003', 'Supporting'],
    ['APP-0004', 'Supporting'],
    ['APP-0031', 'Shared'],
  ],
  'PRD-CHKB': [
    ['APP-0005', 'Primary'],
    ['APP-0006', 'Supporting'],
    ['APP-0004', 'Supporting'],
  ],
  'PRD-BAGJ': [
    ['APP-0007', 'Primary'],
    ['APP-0008', 'Supporting'],
  ],
  'PRD-TURN': [
    ['APP-0009', 'Primary'],
    ['APP-0018', 'Dependency'],
  ],
  'PRD-ARMG': [['APP-0009', 'Primary']],
  'PRD-DISP': [
    ['APP-0015', 'Primary'],
    ['APP-0016', 'Supporting'],
    ['APP-0018', 'Dependency'],
  ],
  'PRD-WBAL': [
    ['APP-0017', 'Primary'],
    ['APP-0005', 'Dependency'],
  ],
  'PRD-FUEL': [
    ['APP-0019', 'Primary'],
    ['APP-0037', 'Dependency'],
  ],
  'PRD-NETP': [
    ['APP-0022', 'Primary'],
    ['APP-0023', 'Supporting'],
  ],
  'PRD-MNTP': [
    ['APP-0020', 'Primary'],
    ['APP-0021', 'Supporting'],
  ],
  'PRD-LOYL': [
    ['APP-0026', 'Primary'],
    ['APP-0028', 'Shared'],
  ],
  'PRD-LPST': [['APP-0027', 'Primary']],
  'PRD-NOTF': [
    ['APP-0029', 'Primary'],
    ['APP-0028', 'Shared'],
  ],
  'PRD-DSCM': [
    ['APP-0029', 'Supporting'],
    ['APP-0030', 'Supporting'],
    ['APP-0028', 'Shared'],
  ],
  'PRD-PRMG': [
    ['APP-0035', 'Primary'],
    ['APP-0002', 'Dependency'],
  ],
  'PRD-ANCL': [
    ['APP-0036', 'Primary'],
    ['APP-0002', 'Dependency'],
  ],
  'PRD-CRGO': [
    ['APP-0024', 'Primary'],
    ['APP-0025', 'Supporting'],
  ],
  'PRD-REVA': [
    ['APP-0033', 'Primary'],
    ['APP-0034', 'Supporting'],
  ],
  'PRD-PAYB': [
    ['APP-0031', 'Primary'],
    ['APP-0032', 'Supporting'],
  ],
  'PRD-C360': [
    ['APP-0037', 'Primary'],
    ['APP-0038', 'Supporting'],
    ['APP-0028', 'Shared'],
  ],
};

/** Curated product → L3 process anchors. The rest are generated. */
const CURATED_PRODUCT_PROCESSES: Record<string, readonly string[]> = {
  'PRD-CDR': [
    'PRC-5.4.1',
    'PRC-5.4.2',
    'PRC-5.4.3',
    'PRC-5.4.4',
    'PRC-5.4.5',
    'PRC-5.4.6',
    'PRC-5.3.2',
    'PRC-4.3.3',
  ],
  'PRD-CRWP': ['PRC-5.2.1', 'PRC-5.2.2', 'PRC-5.2.3', 'PRC-5.2.4', 'PRC-5.2.5', 'PRC-5.1.1'],
  'PRD-CRTR': ['PRC-5.5.1', 'PRC-5.5.2', 'PRC-5.5.3'],
  'PRD-BOOK': ['PRC-2.3.1', 'PRC-2.3.2', 'PRC-2.3.3', 'PRC-2.4.1', 'PRC-2.4.2', 'PRC-2.4.3'],
  'PRD-TRIP': ['PRC-3.1.1', 'PRC-3.1.2', 'PRC-3.1.3', 'PRC-3.1.4'],
  'PRD-CHKB': ['PRC-6.1.1', 'PRC-6.1.2', 'PRC-6.1.4', 'PRC-6.3.1', 'PRC-6.3.2', 'PRC-6.3.3'],
  'PRD-BAGJ': ['PRC-6.2.1', 'PRC-6.2.2', 'PRC-6.2.3', 'PRC-6.2.4', 'PRC-6.1.3'],
  'PRD-TURN': ['PRC-6.4.1', 'PRC-6.4.2', 'PRC-6.4.3'],
  'PRD-ARMG': ['PRC-6.5.1', 'PRC-6.5.2', 'PRC-6.5.3'],
  'PRD-DISP': ['PRC-4.1.1', 'PRC-4.1.2', 'PRC-4.1.3', 'PRC-4.1.4'],
  'PRD-WBAL': ['PRC-4.2.1', 'PRC-4.2.2', 'PRC-4.2.3', 'PRC-4.2.4'],
  'PRD-FUEL': ['PRC-4.1.2'],
  'PRD-NETP': ['PRC-1.2.1', 'PRC-1.2.2', 'PRC-1.2.3', 'PRC-1.2.4'],
  'PRD-MNTP': ['PRC-7.1.1', 'PRC-7.1.2', 'PRC-7.1.3'],
  'PRD-LOYL': ['PRC-3.4.1', 'PRC-3.4.2', 'PRC-3.4.3', 'PRC-3.4.4'],
  'PRD-LPST': ['PRC-3.4.2'],
  'PRD-NOTF': ['PRC-3.3.1', 'PRC-3.3.2', 'PRC-3.3.3', 'PRC-3.3.4'],
  'PRD-DSCM': ['PRC-3.3.1', 'PRC-3.3.3'],
  'PRD-PRMG': ['PRC-2.1.1', 'PRC-2.1.2', 'PRC-2.1.3', 'PRC-2.1.4', 'PRC-2.2.1'],
  'PRD-ANCL': ['PRC-2.6.1', 'PRC-2.6.2', 'PRC-2.6.3'],
  'PRD-CRGO': ['PRC-8.4.1'],
  'PRD-REVA': ['PRC-8.1.1', 'PRC-8.1.2', 'PRC-8.1.3', 'PRC-8.1.4'],
  'PRD-PAYB': ['PRC-8.2.1', 'PRC-8.2.2', 'PRC-8.2.3', 'PRC-8.2.4'],
  'PRD-C360': ['PRC-8.6.1', 'PRC-8.6.2', 'PRC-8.6.3', 'PRC-8.6.4'],
};

interface RelationshipInputs {
  asOf: string;
  rng: Rng;
  products: readonly Product[];
  applications: readonly Application[];
  processes: readonly Process[];
  teams: readonly Team[];
}

export interface RelationshipData {
  productApplicationLinks: ProductApplicationLink[];
  productProcessLinks: ProductProcessLink[];
  applicationProcessLinks: ApplicationProcessLink[];
  teamProductLinks: TeamProductLink[];
  productDependencyLinks: ProductDependencyLink[];
}

export function buildRelationships(inputs: RelationshipInputs): RelationshipData {
  const { asOf, products, applications, processes, teams } = inputs;
  const rng = inputs.rng.fork('relationships');
  const stamp = provenanceFactory(asOf, inputs.rng.fork('relationships-provenance'));

  const activeProducts = products.filter(
    (p) => p.lifecycle === 'Active' || p.lifecycle === 'Sunset',
  );
  const curatedApps = applications.filter((a) => a.isCurated);
  const generatedApps = applications.filter((a) => !a.isCurated);
  const l3 = processes.filter((p) => p.level === 'L3');
  const processByCode = new Map(processes.map((p) => [p.businessCode, p]));

  let sequence = 0;
  const base = (
    kind: string,
    role: RelationshipRole,
    options: { confidence: number; status?: RelationshipStatus; method?: ConfidenceMethod },
  ): Omit<RelationshipBase, 'id' | 'businessCode'> => {
    sequence += 1;
    const confidence = round(options.confidence, 2);
    const band = bandFor(confidence);
    // Status is derived from the band rather than from a second threshold, so
    // the two cannot drift apart. A Low-confidence mapping must never sit in an
    // Approved state: the taxonomy model is explicit that low confidence cannot
    // finalise a material boundary without an explicit exception, and a fixture
    // shipping thousands of them would demonstrate the opposite of the rule the
    // product exists to enforce.
    const status: RelationshipStatus =
      options.status ?? (band === 'High' || band === 'Medium' ? 'Approved' : 'Proposed');
    return {
      role,
      status,
      confidence,
      confidenceScore: confidence,
      confidenceBand: band,
      confidenceMethod:
        options.method ??
        (confidence >= 0.9
          ? 'SystemDerivedExact'
          : confidence >= 0.75
            ? 'RuleDerivedDeterministic'
            : confidence >= 0.55
              ? 'SpecialistReviewed'
              : 'AssistedCandidate'),
      evidenceCode: confidence >= 0.8 ? `EVD-${kind}-${pad(sequence % 40 || 40, 3)}` : null,
      validFrom: '2025-01-01',
      validTo: null,
      isDraft: status === 'Proposed',
      provenance: stamp(SOURCE_SYSTEMS.dataHub, `REL-${kind}-${pad(sequence, 6)}`, {
        meanAgeDays: 12,
        meanConfidence: confidence,
      }),
    };
  };

  // ── Product ↔ Application ─────────────────────────────────────────────────
  const productApplicationLinks: ProductApplicationLink[] = [];
  const pushProductApp = (
    productCode: string,
    applicationCode: string,
    role: RelationshipRole,
    confidence: number,
  ): void => {
    const code = `REL-PA-${productCode}-${applicationCode}`;
    productApplicationLinks.push({
      id: idFor('ProductApplication', code),
      businessCode: code,
      productId: idFor('Product', productCode),
      productCode,
      applicationId: idFor('Application', applicationCode),
      applicationCode,
      allocationPercent: null,
      ...base('PA', role, { confidence }),
    });
  };

  for (const [productCode, links] of Object.entries(CURATED_PRODUCT_APPLICATIONS)) {
    for (const [applicationCode, role] of links) {
      pushProductApp(
        productCode,
        applicationCode,
        role,
        role === 'Primary' ? rng.float(0.9, 0.98) : rng.float(0.6, 0.92),
      );
    }
  }

  // Generated applications fan out across the generated product tail. The 7%
  // orphan slice is carved out first so it cannot be eroded by later passes.
  const orphanCount = Math.round(generatedApps.length * 0.075);
  const shuffledGenerated = rng.shuffle(generatedApps);
  const orphans = new Set(shuffledGenerated.slice(0, orphanCount).map((a) => a.businessCode));
  const generatedProducts = activeProducts.filter((p) => !p.isCurated);

  shuffledGenerated.forEach((app, index) => {
    if (orphans.has(app.businessCode)) return;
    const productCount = rng.weighted([
      [1, 62],
      [2, 24],
      [3, 10],
      [4, 4],
    ] as const);
    // Bias each application towards products in a matching department so the
    // mapping does not read as noise when a domain filter is applied.
    const pool = generatedProducts.length > 0 ? generatedProducts : activeProducts;
    const chosen = new Set<string>();
    for (let i = 0; i < productCount; i += 1) {
      const product = at(pool, (index * 7 + i * 131 + rng.int(0, 5)) % pool.length);
      chosen.add(product.businessCode);
    }
    let first = true;
    for (const productCode of chosen) {
      pushProductApp(
        productCode,
        app.businessCode,
        first ? 'Primary' : rng.pick(['Supporting', 'Shared', 'Dependency'] as const),
        rng.around(0.74, 0.2, 0.24, 0.98),
      );
      first = false;
    }
  });

  // Curated applications also serve part of the generated tail, so the estate
  // does not split cleanly into "curated" and "everything else".
  for (const app of curatedApps) {
    if (!rng.chance(0.45) || generatedProducts.length === 0) continue;
    const product = rng.pick(generatedProducts);
    if (
      productApplicationLinks.some(
        (l) => l.productCode === product.businessCode && l.applicationCode === app.businessCode,
      )
    ) {
      continue;
    }
    pushProductApp(product.businessCode, app.businessCode, 'Shared', rng.float(0.35, 0.7));
  }

  // ── Allocation percentages ────────────────────────────────────────────────
  // Shares are computed per application across its products so they can never
  // exceed 100%. About one application in six is left entirely unallocated and
  // a further slice only partially allocated — that residue is what the cost
  // pages surface as `unallocated`, rather than quietly spreading it.
  const linksByApp = groupBy(productApplicationLinks, (l) => l.applicationCode);
  for (const [, links] of linksByApp) {
    if (rng.chance(0.16)) continue; // allocation not yet agreed — stays null
    const coverage = rng.chance(0.22) ? rng.float(0.55, 0.85) : 1;
    const weights = links.map((link) =>
      link.role === 'Primary' ? 6 : link.role === 'Supporting' ? 3 : 1,
    );
    const weightTotal = weights.reduce((sum, w) => sum + w, 0);
    links.forEach((link, index) => {
      link.allocationPercent = round((at(weights, index) / weightTotal) * coverage, 4);
    });
  }

  // ── Product ↔ Process ─────────────────────────────────────────────────────
  const productProcessLinks: ProductProcessLink[] = [];
  const pushProductProcess = (
    productCode: string,
    processCode: string,
    role: RelationshipRole,
    confidence: number,
  ): void => {
    const code = `REL-PP-${productCode}-${processCode}`;
    productProcessLinks.push({
      id: idFor('ProductProcess', code),
      businessCode: code,
      productId: idFor('Product', productCode),
      productCode,
      processId: idFor('Process', processCode),
      processCode,
      ...base('PP', role, { confidence }),
    });
  };

  for (const [productCode, processCodes] of Object.entries(CURATED_PRODUCT_PROCESSES)) {
    processCodes.forEach((processCode, index) => {
      if (!processByCode.has(processCode)) return;
      pushProductProcess(
        productCode,
        processCode,
        index === 0 ? 'Primary' : 'Supporting',
        index === 0 ? rng.float(0.88, 0.98) : rng.float(0.52, 0.9),
      );
    });
  }

  for (const product of generatedProducts) {
    const count = rng.int(1, 5);
    for (let i = 0; i < count; i += 1) {
      const process = rng.pick(l3);
      const code = `REL-PP-${product.businessCode}-${process.businessCode}`;
      if (productProcessLinks.some((l) => l.businessCode === code)) continue;
      pushProductProcess(
        product.businessCode,
        process.businessCode,
        i === 0 ? 'Primary' : 'Supporting',
        rng.around(0.62, 0.24, 0.18, 0.97),
      );
    }
  }

  // ── Application ↔ Process ─────────────────────────────────────────────────
  // Derived from the two edges above rather than invented, which is how the
  // real engine will infer it — and means the mapping studio's three columns
  // are consistent with each other by construction.
  const applicationProcessLinks: ApplicationProcessLink[] = [];
  const seenAppProcess = new Set<string>();
  const processesByProduct = groupBy(productProcessLinks, (l) => l.productCode);
  for (const link of productApplicationLinks) {
    const processLinks = processesByProduct.get(link.productCode) ?? [];
    for (const processLink of processLinks) {
      if (link.role === 'Dependency' && !rng.chance(0.25)) continue;
      const key = `${link.applicationCode}|${processLink.processCode}`;
      if (seenAppProcess.has(key)) continue;
      seenAppProcess.add(key);
      const code = `REL-AP-${link.applicationCode}-${processLink.processCode}`;
      applicationProcessLinks.push({
        id: idFor('ApplicationProcess', code),
        businessCode: code,
        applicationId: link.applicationId,
        applicationCode: link.applicationCode,
        processId: processLink.processId,
        processCode: processLink.processCode,
        ...base('AP', link.role === 'Primary' ? 'Primary' : 'Supporting', {
          confidence: Math.min(link.confidence, processLink.confidence) * rng.float(0.85, 1),
        }),
      });
    }
  }

  // ── Team ↔ Product ────────────────────────────────────────────────────────
  const teamProductLinks: TeamProductLink[] = [];
  for (const team of teams) {
    const shares = team.productCodes.map(() => rng.float(0.2, 1));
    const total = shares.reduce((sum, s) => sum + s, 0);
    team.productCodes.forEach((productCode, index) => {
      const code = `REL-TP-${team.businessCode}-${productCode}`;
      teamProductLinks.push({
        id: idFor('TeamProduct', code),
        businessCode: code,
        teamId: team.id,
        teamCode: team.businessCode,
        productId: idFor('Product', productCode),
        productCode,
        capacityShare: round(at(shares, index) / total, 4),
        ...base('TP', index === 0 ? 'Primary' : 'Supporting', {
          confidence: rng.float(0.72, 0.97),
        }),
      });
    });
  }

  // ── Product → depended-on platform ────────────────────────────────────────
  const productDependencyLinks: ProductDependencyLink[] = [];
  const dependencyLinks = productApplicationLinks.filter((l) => l.role === 'Dependency');
  for (const link of dependencyLinks) {
    const app = applications.find((a) => a.businessCode === link.applicationCode);
    const criticality: Criticality =
      app?.criticality === 'MissionCritical'
        ? 'MissionCritical'
        : app?.criticality === 'BusinessCritical'
          ? 'BusinessCritical'
          : 'Important';
    const code = `REL-PD-${link.productCode}-${link.applicationCode}`;
    productDependencyLinks.push({
      id: idFor('ProductDependency', code),
      businessCode: code,
      productId: link.productId,
      productCode: link.productCode,
      dependsOnApplicationId: link.applicationId,
      dependsOnApplicationCode: link.applicationCode,
      criticality,
      ...base('PD', 'Dependency', { confidence: link.confidence }),
    });
  }

  return {
    productApplicationLinks,
    productProcessLinks,
    applicationProcessLinks,
    teamProductLinks,
    productDependencyLinks,
  };
}
